import { useEffect, useState, useRef } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";

export interface UserProfile {
  ftuCompleted?: boolean;
  fullName?: string;
  ftuPath?: string;
  email?: string;
  hasVisitedDashboard?: boolean;
  [key: string]: unknown;
}

export function useUserProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const retried = useRef(false);

  useEffect(() => {
    const uid = user?.uid;

    if (!uid) {
      setProfile(null);
      setLoading(false);
      return;
    }

    retried.current = false;
    setLoading(true);

    let unsub: () => void;

    const subscribe = () => {
      const ref = doc(db, "users", uid);
      unsub = onSnapshot(
        ref,
        (snap) => {
          if (snap.exists()) {
            setProfile(snap.data() as UserProfile);
          } else {
            setProfile(null);
          }
          setLoading(false);
        },
        (err) => {
          if (err.code === "permission-denied" && !retried.current) {
            retried.current = true;
            // Token not yet propagated to Firestore SDK — retry after short delay
            setTimeout(() => {
              unsub?.();
              subscribe();
            }, 1500);
          } else {
            if (err.code !== "permission-denied") {
              console.error("useUserProfile critical error:", err);
            }
            setProfile(null);
            setLoading(false);
          }
        }
      );
    };

    subscribe();

    return () => unsub?.();
  }, [user?.uid]);

  return { profile, loading };
}